import React, { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import {
  callBanner,
  TargetParams,
  WindowWithAdriver,
} from '@/components/Banners/adriver';
import { useBannerStore } from '@/store/useBannerStore/useBannerStore';
import { handleAndLoadBanner } from '@/components/Banners/AdriverBanner.helpers';
import { BannerName } from '@/components/Banners/Banners.types';
import { AdriverBannerId } from '@/components/Banners/Banners.constants';
import {
  BannerSource,
  SetBannerSource,
} from '@/store/useBannerStore/useBannerStore.types';
import { sleep } from '@/utils/helpers';

interface BannerProps {
  containerId: string;
  containerClassName?: string;
  tagName: string;
  sid: string;
  bn: string;
  bt?: string;
  arCn?: number;
  targetParams?: TargetParams;
  isAjax?: boolean;
}

export const AdriverBanner: React.FC<BannerProps> = ({
  containerId,
  containerClassName,
  tagName,
  sid,
  bn,
  bt = '43',
  arCn,
  targetParams,
  isAjax,
}) => {
  const pathname = usePathname();
  const tag = tagName || 'div';

  const [
    isBlockerDetected,
    bannerTopSource,
    bannerBottomSource,
    bannerBackgroundSource,
    setBannerTopSource,
    setBannerBottomSource,
    setBannerBackgroundSource,
    setIsAdriverTopBannerVisible,
  ] = useBannerStore(state => [
    state.isBlockerDetected,
    state.bannerTopSource,
    state.bannerBottomSource,
    state.bannerBackgroundSource,
    state.setBannerTopSource,
    state.setBannerBottomSource,
    state.setBannerBackgroundSource,
    state.setIsAdriverTopBannerVisible,
  ]);

  function handleBannerLoadState(
    bannerType: keyof typeof AdriverBannerId,
    loaded: boolean
  ) {
    if (bannerType === AdriverBannerId[BannerName.Top]) {
      setIsAdriverTopBannerVisible(loaded);
    }
  }

  async function checkBanner(
    selector: string,
    setBannerSource: SetBannerSource,
    signal: AbortSignal
  ) {
    await sleep(5_000);

    if (signal.aborted) return;

    const banner = document.querySelector(selector);
    const isBannerLoaded = !!banner?.clientHeight && banner.clientHeight > 0;

    if (!isBannerLoaded) {
      setBannerSource(BannerSource.Admanager);
    }
  }

  useEffect(() => {
    if (!isBlockerDetected) {
      callBanner({
        container_id: containerId,
        sid,
        bn,
        bt,
        arCn,
        target: targetParams,
        isAjax,
      });
    } else {
      typeof (window as WindowWithAdriver).adriverExtentionLoad !==
        'undefined' &&
        (window as WindowWithAdriver).adriverExtentionLoad?.(
          0,
          null,
          containerId
        );
    }
  }, [pathname]);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    if (containerId === AdriverBannerId[BannerName.Top]) {
      setIsAdriverTopBannerVisible(false);

      handleAndLoadBanner(
        BannerName.Top,
        setBannerTopSource,
        bannerTopSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-top-banner', setBannerTopSource, signal);
    }

    if (containerId === AdriverBannerId[BannerName.Bottom]) {
      handleAndLoadBanner(
        BannerName.Bottom,
        setBannerBottomSource,
        bannerBottomSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-bottom-banner', setBannerBottomSource, signal);
    }

    if (containerId === AdriverBannerId[BannerName.Background]) {
      handleAndLoadBanner(
        BannerName.Background,
        setBannerBackgroundSource,
        bannerBackgroundSource === BannerSource.Admanager,
        handleBannerLoadState
      );
      checkBanner('.lotriver-bg-left', setBannerBackgroundSource, signal);
    }

    return () => {
      abortController.abort();
    };
  }, [pathname]);

  return React.createElement(
    tag,
    { id: containerId, className: containerClassName },
    null
  );
};
